import React from "react";
import { createRoot } from "react-dom/client";
import {
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
} from "@clerk/clerk-react";
import App from "./App";
import ClerkTokenProvider from "./components/ClerkTokenProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import { initTheme } from "./lib/theme";
import "./styles.css";

// Theme/brightness are applied pre-hydration by the inline script in
// index.html to avoid FOUC. This call re-syncs the JS-side state and
// subscribes to the system-preference media query.
initTheme();

// Clerk is opt-in at build time: when VITE_CLERK_PUBLISHABLE_KEY isn't
// defined (local dev without Clerk configured, or older deploys that
// predate the Clerk cutover), render the app exactly as before. Only
// when the key is set do we wrap in <ClerkProvider> with a loading
// fallback so the UI never flashes pre-hydrated state while Clerk's
// JS bootstraps.
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

const appTree = (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

const loadingFallback = (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      background: "var(--bg-primary, #1a1a1a)",
      color: "var(--text-primary, #ccc)",
    }}
  >
    Loading…
  </div>
);

const rootContent = clerkPublishableKey ? (
  <ClerkProvider publishableKey={clerkPublishableKey}>
    <ClerkLoading>{loadingFallback}</ClerkLoading>
    <ClerkLoaded>
      {/* ClerkTokenProvider uses useAuth() internally, so it MUST be
          mounted inside ClerkProvider/ClerkLoaded. The no-key fallback
          path below skips it — api.ts will then send requests with no
          Authorization header (the Worker 401s, the user is redirected
          to /welcome). */}
      <ClerkTokenProvider>{appTree}</ClerkTokenProvider>
    </ClerkLoaded>
  </ClerkProvider>
) : (
  appTree
);

const root = document.getElementById("root");
if (!root) throw new Error("root element missing");
createRoot(root).render(<React.StrictMode>{rootContent}</React.StrictMode>);
